import { useStatusOverlay } from '@fs/zion-ui'
import { useTranslation } from 'react-i18next'
import { useHistory } from '@fs/zion-router'
import { type UseMutateAsyncFunction, useMutation, type UseMutationResult, useQueryClient } from '@tanstack/react-query'
import { type AxiosError, type AxiosResponse } from '@fs/zion-axios'
import { playlistsConfig } from '../../config'
import type { Playlist } from '../../api/rootstech/playlist/playlist.types'
import { createPlaylist as createPlaylistApi } from '../../api'
import notifySentry from '../../lib/helpers/sentryNotifier'

type CreatePlaylistParams = {
  name: string
  description: string
  initialItem?: string
}

type UsePlaylistCreation = Omit<
  UseMutationResult<AxiosResponse<Playlist>, AxiosError, CreatePlaylistParams>,
  'mutateAsync'
> & {
  createPlaylist: UseMutateAsyncFunction<AxiosResponse<Playlist>, AxiosError, CreatePlaylistParams>
}

export const usePlaylistCreation = (): UsePlaylistCreation => {
  const showStatusOverlay = useStatusOverlay()
  const [t] = useTranslation()
  const history = useHistory()
  const queryClient = useQueryClient()

  const { mutateAsync: createPlaylist, ...rest } = useMutation<
    AxiosResponse<Playlist>,
    AxiosError,
    CreatePlaylistParams
  >({
    mutationFn: async (data) => createPlaylistApi({ data }),
    onSuccess: ({ data: createdPlaylist }) => {
      // Sync state without having to refetch
      queryClient.setQueryData<AxiosResponse<{ results: Playlist[] }>>(
        ['playlists'],
        (oldResponse: { data: { results: Playlist[] } }) => {
          return {
            ...oldResponse,
            data: {
              ...oldResponse.data,
              results: [...oldResponse.data.results, createdPlaylist],
            },
          }
        }
      )

      const { name, id } = createdPlaylist

      showStatusOverlay({
        message: t('playlists.alerts.created-new', 'Created playlist { playlistName }.', { playlistName: name }),
        type: 'success',
        actionLabel: t('playlists.saved.nav-to-playlist', 'Go to Playlist'),
        actionCallback: () => history.push(`/playlists/${id}`),
      })
    },
    onError: (err: AxiosError) => {
      let message = t(
        'playlists.errors.generic.creation-error',
        "We're sorry, but there was an issue creating your new playlist. Please try again shortly or refresh your browser."
      )
      if (err.response?.status === 422) {
        message = t(
          'playlists.alerts.playlist-limit-reached',
          'You have reached the maximum limit of {max} playlists. Please delete any old or non-essential playlists to make room for new ones.',
          {
            max: playlistsConfig.playlistLimit,
          }
        )
      } else {
        notifySentry({ ...err.response, err })
      }

      showStatusOverlay({
        message,
        type: 'error',
        dismissable: true,
      })
    },
  })
  return { createPlaylist, ...rest }
}
